import { Alert, Button, Divider, Flex, Radio, Select, Tabs, Text } from "@theorgchartdev/storybook";
import * as StateLib from "@/Utils/StateLib";
import { GetBGMgr, GetDoc, GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import React, { Fragment, useState } from "react";
import cx from "classnames";
import { PageProperties } from "@/appComponents/Navigation/RightSidebar/PageProperties/PageProperties";
import { useAsyncEffect } from "@/hooks/useAsyncEffect";
import { useRouteState } from "@/hooks/useRouteState";
import { LoadDirectory } from "@/Utils/Promises";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { useTopic } from "@/hooks/useTopic";
import {
  BuildBackgroundOptions,
  DoBackgroundSavePrompt,
  getCurrentAppliedBackground
} from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/Backgrounds.utils";
import { BackgroundElements } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/BackgroundElements";
import { useBackgroundController } from "@/appComponents/Navigation/RightSidebar/BackgroundDisplay/useBackgroundController";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { appstr } from "@coreapp/utils/appstr";
import { Globals } from "@coreapp/CoreApp/Globals";
import { StripExtension } from "@coreapp/utils/StripExtension";
import { IsBackgroundFile } from "@coreapp/utils/IsBackgroundFile";
import { enumBGOption, enumFeaturesList, enumUIModes } from "@coreapp/constants";
import { getFeatureAccess } from "@/Utils/Misc";

export const BackgroundTabs = ({ doc }) => {
  const workspaceMgr = GetWSMgr();
  const { setParam: setRouteParam } = useRouteState();
  const [currentTab, setCurrentTab] = useState(0);

  const [currentBackgroundName, setCurrentBackgroundName] = useState(
    () => getCurrentAppliedBackground() ?? "None"
  );
  useTopic("BackgroundNameChange", () => {
    setCurrentBackgroundName(getCurrentAppliedBackground() ?? "None");
  });

  const { data, loading } = useAsyncEffect(async () => {
    const result = await LoadDirectory("Masters", "Masters");
    return result.processedResults;
  }, [currentBackgroundName]);

  const { backgroundArea, ...backgroundCtrl } = useBackgroundController({
    currentBackgroundName
  });

  const handleTabChange = currentTabIndex => {
    setCurrentTab(currentTabIndex);
  };

  const ws = GetWSMgr();
  const bgMgr = GetBGMgr();
  const docName = ws?.GetFileName(true);
  const document = GetDoc();
  const isBackgroundFile = IsBackgroundFile(docName);
  const iWizData = document?.GetIWizData();
  const ruleDrivenBackground = bgMgr?.IsViewAppliedBackground() ?? false;
  const { canModifyBackgrounds } = usePermissionCheck();
  const showBgEdit = canModifyBackgrounds && Globals._uiConfig.InBackgroundEditorMode();
  const showAddBgButton = showBgEdit && !ruleDrivenBackground;

  let mpOption = iWizData?.GetMasterOption();
  if (mpOption == null) {
    mpOption = enumBGOption.custom;
    iWizData?.SetMasterOption(mpOption);
  }

  const SetDocDirty = () => {
    const doc = GetDoc();
    const setDirty = Globals._uiConfig.GetUIMode() === enumUIModes.edit;
    doc.SetDirty(setDirty);
  };

  const readyToChangeBackground = callbackFn => {
    const wsMgr = GetWSMgr();
    if (!wsMgr.IsBackgroundDirty()) {
      return true;
    }
    DoBackgroundSavePrompt(callbackFn, callbackFn);
    return false;
  };

  const GetNewBackgroundName = () => {
    const backgrounds = data ?? [];
    const names = backgrounds.map(item => StripExtension(item));
    return names.GetUniqueDuplicateName(appstr("Untitled"), "");
  };

  const CreateNewBackground = () => {
    const bgMgr = GetBGMgr();
    const doc = GetDoc();
    const wsMgr = GetWSMgr();

    if (wsMgr.IsBackgroundDirty()) {
      DoBackgroundSavePrompt(CreateNewBackground, CreateNewBackground);
      return;
    }

    const newBackgroundName = GetNewBackgroundName();
    const filename = `${newBackgroundName}.ocb`;

    //iWizData.SetMasterFile(`${filename}.ocb`);
    if (doc.IsServerChart()) {
      iWizData.SetMasterOption(enumBGOption.custom);
      bgMgr.ClearActiveBackground();
    } else {
      SetCustomBackground(true);
    }

    bgMgr.SetCurBackgroundName(filename);
    wsMgr.SetBackgroundDirty(true);
  };

  const SetCustomBackground = resetCurrentBackground => {
    const wsMgr = GetWSMgr();
    const bgMgr = GetBGMgr();
    const doc = GetDoc();
    const iWizData = doc.GetIWizData();
    iWizData.SetMasterOption(enumBGOption.custom);
    if (resetCurrentBackground) {
      bgMgr.ClearActiveBackground();
      wsMgr.ApplyBackground();
    }
    ReRenderCanvas();
  };

  // URL-driven: writing ?background=... is the single trigger. useBackgroundSync
  // reads the param and drives the iWizData + wsMgr.ApplyBackground() sequence.
  // This click handler only pushes to the URL and handles doc-dirty bookkeeping.
  const SetLinkedBackground = option => {
    const proceed = readyToChangeBackground(() => SetLinkedBackground(option));
    if (!proceed) return;
    setRouteParam("background", option.value);
    SetDocDirty();
  };

  const toggleCustomBackground = () => {
    if (mpOption === enumBGOption.custom) return;
    SetCustomBackground(false);
    SetDocDirty();
    ReRenderCanvas();
  };

  const toggleUseExistingBackground = () => {
    const proceed = readyToChangeBackground(() => toggleUseExistingBackground());
    if (!proceed) return;
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const iWizData = doc.GetIWizData();
    const bgMgr = GetBGMgr();
    iWizData.SetMasterOption(enumBGOption.linked);
    var currentBG = iWizData.GetMasterFile();
    if (currentBG != null) {
      bgMgr.BustMasterCache(currentBG);
      wsMgr.ApplyBackground();
    } else {
      bgMgr.ClearActiveBackground();
    }
    SetDocDirty();
    ReRenderCanvas();
  };

  const getSelectLabel = () => {
    let curApplied = currentBackgroundName;
    const wsMgr = GetWSMgr();
    const doc = GetDoc();
    const iWiz = doc?.GetIWizData();
    if (!iWiz) {
      return appstr("No background");
    }

    const background = iWiz.GetMasterFile();
    const filename = wsMgr.GetFileName(true);
    if (
      mpOption === enumBGOption.custom &&
      !document.IsServerChart() &&
      !IsBackgroundFile(filename)
    ) {
      curApplied = background;
    }

    return curApplied && curApplied !== "None"
      ? StripExtension(curApplied)
      : appstr("No background");
  };

  const getSelectTip = () => {
    return ruleDrivenBackground
      ? { title: appstr("Rule driven background"), message: appstr("View controls background") }
      : null;
  };

  const disableBackgroundSelection = () => {
    if (ruleDrivenBackground || loading) return true;

    if (
      (!document?.IsServerChart() && mpOption !== enumBGOption.linked) ||
      !getFeatureAccess(enumFeaturesList.switchBackgrounds)
    )
      return true;

    return false;
  };

  const applySelectHOffset = !document?.IsServerChart() && !ruleDrivenBackground;

  const GetTabs = () => {
    let tabs = [];
    let backgroundTab = {};
    if (showBgEdit) {
      backgroundTab.header = appstr("Elements");
      backgroundTab.dataTestId = `background-elements-tab`;
      backgroundTab.content = (
        <BackgroundElements
          doc={document}
          backgroundCtrl={backgroundCtrl}
          backgroundArea={backgroundArea}
          currentBackgroundName={currentBackgroundName}
        />
      );
      tabs.push(backgroundTab);
    }

    let pagePropTab = {};
    pagePropTab.header = appstr("Page properties");
    pagePropTab.dataTestId = `background-properties-tab`;
    pagePropTab.content = <PageProperties doc={document} />;
    tabs.push(pagePropTab);

    return tabs;
  };

  return (
    <Flex direction={"column"} width={"100%"} height={"100%"} overflow={"hidden"}>
      <Flex direction={"column"} gap={"12px"} padding={"16px"}>
        {showAddBgButton && (
          <Fragment>
            <div>
              <Button
                dataTestId={"create-bg-btn"}
                iconProps={{ name: "plus-solid" }}
                onClick={CreateNewBackground}
                size={"small"}
                modifier={"secondary"}
              >
                {appstr("createnew")}
              </Button>
            </div>
            <Divider vertical={false} />
          </Fragment>
        )}
        {isBackgroundFile ? (
          <Text size={"small"}>{workspaceMgr?.GetFileName()}</Text>
        ) : (
          <Flex direction={"column"} gap={"8px"}>
            {document?.IsServerChart() || ruleDrivenBackground ? (
              <Text size={"small"} modifier={"bold"}>
                {appstr("Selected background")}
              </Text>
            ) : (
              <Fragment>
                <Radio
                  id={"rsb-background-radio--custom"}
                  data-testid={"rsb-background-radio--custom"}
                  key={0}
                  label={appstr("custommaster")}
                  value={enumBGOption.custom}
                  checked={mpOption === enumBGOption.custom}
                  onChange={toggleCustomBackground}
                />
                <Radio
                  id={"rsb-background-radio--linked"}
                  data-testid={"rsb-background-radio--linked"}
                  key={1}
                  label={appstr("Use existing background")}
                  value={enumBGOption.linked}
                  checked={mpOption === enumBGOption.linked}
                  onChange={toggleUseExistingBackground}
                />
              </Fragment>
            )}
            <Flex
              className={"oc-right-justify-select"}
              padding={applySelectHOffset ? "0 0 0 32px" : null}
            >
              <Select
                optionSize="small"
                disabled={disableBackgroundSelection()}
                placeholder={getSelectLabel()}
                tooltipProps={getSelectTip()}
                value={currentBackgroundName}
                options={BuildBackgroundOptions(
                  "background-select",
                  data ?? [],
                  SetLinkedBackground
                )}
                listProps={FieldSelectListProps({
                  inputProps: { dataTestId: "background-select--search" }
                })}
                position="bottom-end"
                dataTestId={"background-select"}
              />
            </Flex>
            {ruleDrivenBackground && (
              <div data-testid="background-rule-driven-alert">
                <Alert
                  severity={"warning"}
                  withBorder
                  showIcon
                  className={"alert-small-text"}
                  alertActions={{ closeAction: () => {} }}
                >
                  {appstr("Cannot edit rule background") +
                    " (" +
                    (document?.GetActiveTemplate?.()?.GetTemplateName?.() ?? "") +
                    ") "}
                </Alert>
              </div>
            )}
          </Flex>
        )}
      </Flex>
      {showBgEdit && (
        <div className={cx("ocn-chart-explorer-column", "ocn-chart-explorer-tabular")}>
          <Tabs
            tabSize={"medium"}
            tabIndex={currentTab}
            customClass={"ocn-sidebar-tabs"}
            customHeaderClass={"oc-tab-header-no-divider"}
            customContentClass={"ocn-chart-explorer-tabs-content"}
            tabs={GetTabs()}
            onTabChange={handleTabChange}
          />
        </div>
      )}
    </Flex>
  );
};
